import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Pagination,
  Snackbar,
  Divider,
  Chip,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { HIERARCHY_REGISTER, OFFICE_REGISTER, OFFICE_EDIT, ARCHIVED_CASES } from '../../routes/routes';
import OfficeCard from '../../components/Card/Office/OfficeCard';
import ConfirmationModal from '../../components/Dialog/Confirmation/ConfirmationDialog';
import { getSignedImageUrl } from '../../constants/CommonFunctions';
import HierarchyCard from '../../components/Card/Hierarchy/HierarchyCard';
import ColumnLabel from '../../components/ui/ColumnLabel';
import EmptyState from '../../components/ui/EmptyState';
import DashedActionCard from '../../components/ui/DashedActionCard';
import { tokens } from '../../theme/tokens';
import { usePerson } from '../../store/personContext';

const itemsPerPage = 6;

// full_name = "nombres;apellidos"
const getDisplayName = (fullName) => {
  if (!fullName) return '';
  const [names, surnames] = fullName.split(';').map((s) => s?.trim() ?? '');
  const firstName = names?.split(' ')[0] ?? '';
  const firstSurname = surnames?.split(' ')[0] ?? '';
  return `${firstName} ${firstSurname}`.trim() || names || '';
};

const OfficeListView = () => {
  const navigate = useNavigate();
  const { person } = usePerson();

  // ---- estado original (sin cambios de lógica) ----
  const [ownedOffices, setOwnedOffices] = useState([]);
  const [memberOffices, setMemberOffices] = useState([]);
  const [hierarchies, setHierarchies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentOwnedPage, setCurrentOwnedPage] = useState(1);
  const [currentMemberPage, setCurrentMemberPage] = useState(1);
  const [currentHierarchyPage, setCurrentHierarchyPage] = useState(1);
  const [isOwner, setIsOwner] = useState(false);
  const [organizationLimit, setOrganizationLimit] = useState(0);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    officeId: null,
    officeName: '',
    loading: false,
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ---- estado nuevo: panel de repositorio ----
  const [repoCases, setRepoCases] = useState([]);
  const [repoLoading, setRepoLoading] = useState(true);
  const [repoSearch, setRepoSearch] = useState('');
  const [repoOfficeFilter, setRepoOfficeFilter] = useState('');

  // ---- estado nuevo: stats de cada office card (activos/expedientes/miembros/facturado) ----
  const [officeStats, setOfficeStats] = useState({});

  const processOfficeImages = useCallback(async (offices) => {
    return Promise.all(
      offices.map(async (office) => {
        if (office.icon_url && !office.icon_url.includes('token=')) {
          try {
            const signedUrl = await getSignedImageUrl(office.icon_url);
            return { ...office, icon_url: signedUrl };
          } catch (error) {
            console.error('Error getting signed URL for office:', office.id, error);
            return office;
          }
        }
        return office;
      })
    );
  }, []);

  useEffect(() => {
    const fetchUserOffices = async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        alert('Usuario no autenticado');
        return;
      }

      setLoading(true);
      try {
        const { data: userOrganization, error: orgError } = await supabase
          .from('organization')
          .select('id, owner_id, limit')
          .eq('owner_id', user.id)
          .maybeSingle();

        if (orgError && orgError.code !== 'PGRST116') {
          console.error('Error fetching user organization:', orgError.message);
        }

        const hasOrganization = !!userOrganization;
        setIsOwner(hasOrganization);
        setOrganizationLimit(userOrganization?.limit || 0);

        if (hasOrganization) {
          const { data: ownedOfficesData, error: ownedError } = await supabase
            .from('office')
            .select('*, organization!inner(id, owner_id)')
            .eq('organization.owner_id', user.id)
            .order('created_at', { ascending: false });

          if (ownedError) {
            console.error('Error fetching owned offices:', ownedError.message);
          } else if (ownedOfficesData) {
            setOwnedOffices(await processOfficeImages(ownedOfficesData));
          }
        }

        let memberQuery = supabase
          .from('office_member')
          .select(
            `
            created_at,
            office_id,
            office:office_id (
              *,
              organization ( id, owner_id, name )
            )
          `
          )
          .eq('member_id', user.id);

        if (hasOrganization) {
          memberQuery = memberQuery.neq('office.organization.owner_id', user.id);
        }

        const { data: memberOfficesData, error: memberError } = await memberQuery.order(
          'created_at',
          { ascending: false }
        );

        if (memberError) {
          console.error('Error fetching member offices:', memberError.message);
        } else if (memberOfficesData) {
          const transformedMemberOffices = memberOfficesData.map((item) => ({
            ...item.office,
            isMember: true,
          }));
          setMemberOffices(await processOfficeImages(transformedMemberOffices));
        }

        if (hasOrganization) {
          const { data: hierarchyData, error: hierarchyError } = await supabase.from('hierarchy')
            .select(`
            id,
            name,
            practice_area ( id, name ),
            hierarchy_form (
              id,
              name,
              case_type ( id, name )
            )
          `);

          if (hierarchyError) {
            console.error('Error fetching hierarchies:', hierarchyError.message);
          } else {
            setHierarchies(hierarchyData || []);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserOffices();
  }, [processOfficeImages]);

  // ---- fetch del panel de repositorio (casos completados de los
  // despachos a los que el usuario tiene acceso) ----
  useEffect(() => {
    if (loading) return;

    const officeIds = [...ownedOffices, ...memberOffices].map((o) => o.id);
    if (officeIds.length === 0) {
      setRepoCases([]);
      setRepoLoading(false);
      return;
    }

    const fetchRepoCases = async () => {
      setRepoLoading(true);
      try {
        const { data: casesData, error } = await supabase
          .from('case')
          .select('id, number, title, office_id, end_date, client_id, client_str, office_practice_area_id')
          .eq('state', 'completed')
          .in('office_id', officeIds)
          .order('end_date', { ascending: false });

        if (error) throw error;

        const clientIds = [...new Set((casesData || []).map((c) => c.client_id).filter(Boolean))];
        const areaIds = [
          ...new Set((casesData || []).map((c) => c.office_practice_area_id).filter(Boolean)),
        ];

        const [{ data: clients }, { data: areas }] = await Promise.all([
          clientIds.length
            ? supabase.from('person').select('id, full_name').in('id', clientIds)
            : Promise.resolve({ data: [] }),
          areaIds.length
            ? supabase.from('office_practice_area').select('id, name').in('id', areaIds)
            : Promise.resolve({ data: [] }),
        ]);

        const clientsById = Object.fromEntries((clients || []).map((p) => [p.id, p]));
        const areasById = Object.fromEntries((areas || []).map((a) => [a.id, a]));

        const enriched = (casesData || []).map((c) => ({
          ...c,
          client: c.client_id ? clientsById[c.client_id] : null,
          office_practice_area: c.office_practice_area_id ? areasById[c.office_practice_area_id] : null,
        }));

        setRepoCases(enriched);
      } catch (error) {
        console.error('Error cargando repositorio:', error.message);
      } finally {
        setRepoLoading(false);
      }
    };

    fetchRepoCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, ownedOffices, memberOffices]);

  // ---- fetch de stats por despacho (activos, expedientes, facturado,
  // miembros + equipo) para las office cards ----
  useEffect(() => {
    if (loading) return;

    const officeIds = [...ownedOffices, ...memberOffices].map((o) => o.id);
    if (officeIds.length === 0) {
      setOfficeStats({});
      return;
    }

    const fetchOfficeStats = async () => {
      try {
        const [{ data: casesData, error: casesError }, { data: membersData, error: membersError }] =
          await Promise.all([
            supabase.from('case').select('office_id, state, billed_amount').in('office_id', officeIds),
            supabase
              .from('office_member')
              .select('office_id, person:member_id(full_name)')
              .in('office_id', officeIds),
          ]);

        if (casesError) throw casesError;
        if (membersError) throw membersError;

        const stats = Object.fromEntries(
          officeIds.map((id) => [id, { active: 0, total: 0, billed: 0, members: 0, team: [] }])
        );

        (casesData || []).forEach((c) => {
          const s = stats[c.office_id];
          if (!s) return;
          s.total += 1;
          if (c.state !== 'completed') s.active += 1;
          s.billed += c.billed_amount || 0;
        });

        (membersData || []).forEach((m) => {
          const s = stats[m.office_id];
          if (!s) return;
          s.members += 1;
          if (m.person?.full_name) s.team.push(getDisplayName(m.person.full_name));
        });

        setOfficeStats(stats);
      } catch (error) {
        console.error('Error cargando stats de despachos:', error.message);
      }
    };

    fetchOfficeStats();
  }, [loading, ownedOffices, memberOffices]);

  const allOffices = useMemo(() => {
    const combined = [...ownedOffices, ...memberOffices].map((o) => ({
      id: o.id,
      name: o.name,
      color: o.color,
    }));
    // Un despacho propio también existe como fila en office_member (se
    // agrega como admin al crearlo), así que puede aparecer en ambas listas.
    return Array.from(new Map(combined.map((o) => [o.id, o])).values());
  }, [ownedOffices, memberOffices]);

  const filteredRepoCases = useMemo(() => {
    return repoCases.filter((c) => {
      if (repoOfficeFilter && c.office_id !== repoOfficeFilter) return false;
      if (repoSearch) {
        const q = repoSearch.toLowerCase();
        const clientName = c.client?.full_name || c.client_str || '';
        return (
          c.title?.toLowerCase().includes(q) ||
          c.number?.toLowerCase().includes(q) ||
          clientName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [repoCases, repoOfficeFilter, repoSearch]);

  const handleOwnedPageChange = (event, value) => setCurrentOwnedPage(value);
  const handleMemberPageChange = (event, value) => setCurrentMemberPage(value);
  const handleHierarchyPageChange = (event, value) => setCurrentHierarchyPage(value);

  const handleEditOffice = (officeId) => {
    navigate(OFFICE_EDIT.replace(':officeId', officeId));
  };

  const handleDeleteOffice = async (officeId) => {
    const office = ownedOffices.find((o) => o.id === officeId);
    if (!office) {
      setNotification({
        open: true,
        message: 'No tienes permisos para eliminar esta oficina',
        severity: 'error',
      });
      return;
    }
    setDeleteModal({
      open: true,
      officeId,
      officeName: office?.name || 'este despacho',
      loading: false,
    });
  };

  const confirmDeleteOffice = async () => {
    setDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      const { error } = await supabase.from('office').delete().eq('id', deleteModal.officeId);
      if (error) throw new Error(error.message);

      setOwnedOffices((prev) => prev.filter((o) => o.id !== deleteModal.officeId));

      const remainingOffices = ownedOffices.length - 1;
      const maxPageAfterDelete = Math.ceil(remainingOffices / itemsPerPage);
      if (currentOwnedPage > maxPageAfterDelete && maxPageAfterDelete > 0) {
        setCurrentOwnedPage(maxPageAfterDelete);
      }

      setNotification({
        open: true,
        message: `El despacho "${deleteModal.officeName}" ha sido eliminado correctamente`,
        severity: 'success',
      });
      setDeleteModal({ open: false, officeId: null, officeName: '', loading: false });
    } catch (error) {
      console.error('Error deleting office:', error.message);
      setNotification({
        open: true,
        message: `Error al eliminar el despacho: ${error.message}`,
        severity: 'error',
      });
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const cancelDeleteOffice = () =>
    setDeleteModal({ open: false, officeId: null, officeName: '', loading: false });

  const closeNotification = () => setNotification((prev) => ({ ...prev, open: false }));

  const startOwnedIndex = (currentOwnedPage - 1) * itemsPerPage;
  const currentOwnedOffices = ownedOffices.slice(startOwnedIndex, startOwnedIndex + itemsPerPage);
  const totalOwnedPages = Math.ceil(ownedOffices.length / itemsPerPage);

  const startMemberIndex = (currentMemberPage - 1) * itemsPerPage;
  const currentMemberOffices = memberOffices.slice(startMemberIndex, startMemberIndex + itemsPerPage);
  const totalMemberPages = Math.ceil(memberOffices.length / itemsPerPage);

  const startHierarchyIndex = (currentHierarchyPage - 1) * itemsPerPage;
  const currentHierarchies = hierarchies.slice(startHierarchyIndex, startHierarchyIndex + itemsPerPage);
  const totalHierarchyPages = Math.ceil(hierarchies.length / itemsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Saludo (reemplaza el header 'Despachos' + chip de rol, que ahora vive en el TopBar) */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold">
          Bienvenido,{' '}
          <Box component="span" sx={{ color: tokens.colors.indigo }}>
            {(person?.full_name || '').split(' ')[0].replace(/[^\p{L}\p{N}]+$/u, '') || ''}
          </Box>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Elige el despacho con el que quieres trabajar o consulta el archivo de casos
          finalizados.
        </Typography>
      </Box>

      {isOwner && organizationLimit > 0 && ownedOffices.length >= organizationLimit && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Has alcanzado el límite de {organizationLimit} oficinas para tu organización. Contacta al
          administrador para aumentar el límite.
        </Alert>
      )}
      {isOwner && organizationLimit === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No tienes límite configurado para crear oficinas. Contacta al administrador para
          configurar tu límite.
        </Alert>
      )}

      {/* Layout de dos columnas */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.35fr) minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* ---- Columna izquierda: despachos + jerarquías ---- */}
        <Box>
          {isOwner && (
            <Box mb={5}>
              <ColumnLabel>Mis Despachos</ColumnLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {`Despachos de tu organización (${ownedOffices.length}${
                  organizationLimit > 0 ? `/${organizationLimit}` : ''
                })`}
              </Typography>
              {ownedOffices.length === 0 ? (
                <EmptyState type="owned" />
              ) : (
                <>
                  <Grid container spacing={2.5}>
                    {currentOwnedOffices.map((office) => (
                      <Grid item size={12} key={office.id}>
                        <OfficeCard
                          office={office}
                          roleLabel="Propietario"
                          showActions
                          onEdit={handleEditOffice}
                          onDelete={handleDeleteOffice}
                          stats={officeStats[office.id]}
                          team={officeStats[office.id]?.team}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {ownedOffices.length < organizationLimit && (
                    <Box sx={{ mt: 2.5 }}>
                      <DashedActionCard
                        icon={<AddRoundedIcon />}
                        title="Añadir despacho"
                        onClick={() => navigate(OFFICE_REGISTER)}
                      />
                    </Box>
                  )}
                  {totalOwnedPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                      <Pagination
                        count={totalOwnedPages}
                        page={currentOwnedPage}
                        onChange={handleOwnedPageChange}
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {isOwner && memberOffices.length > 0 && <Divider sx={{ mb: 5 }} />}

          {memberOffices.length > 0 && (
            <Box mb={5}>
              <ColumnLabel>Despachos como Miembro</ColumnLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {`Despachos donde tienes acceso como miembro (${memberOffices.length})`}
              </Typography>
              <Grid container spacing={2.5}>
                {currentMemberOffices.map((office) => (
                  <Grid item size={12} key={office.id}>
                    <OfficeCard
                      office={office}
                      roleLabel="Miembro"
                      stats={officeStats[office.id]}
                      team={officeStats[office.id]?.team}
                    />
                  </Grid>
                ))}
              </Grid>
              {totalMemberPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalMemberPages}
                    page={currentMemberPage}
                    onChange={handleMemberPageChange}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}

          {!isOwner && memberOffices.length === 0 && <EmptyState type="member" />}

          {isOwner && hierarchies.length > 0 && <Divider sx={{ mb: 5 }} />}

          {isOwner && (
          <Box>
            <ColumnLabel>Jerarquías</ColumnLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {`Configuraciones disponibles (${hierarchies.length})`}
            </Typography>
            {hierarchies.length === 0 ? (
              <EmptyState type="hierarchy" />
            ) : (
              <>
                <Grid container spacing={2.5}>
                  {currentHierarchies.map((hierarchy) => (
                    <Grid item size={{ xs: 12, sm: 6 }} key={hierarchy.id}>
                      <HierarchyCard hierarchy={hierarchy} />
                    </Grid>
                  ))}
                </Grid>
                {hierarchies.length > 0 && (
                  <Box sx={{ mt: 2.5 }}>
                    <DashedActionCard
                      icon={<AddRoundedIcon />}
                      title="Nueva Jerarquía"
                      onClick={() => navigate(HIERARCHY_REGISTER)}
                    />
                  </Box>
                )}
                {totalHierarchyPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={totalHierarchyPages}
                      page={currentHierarchyPage}
                      onChange={handleHierarchyPageChange}
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          )}
        </Box>

        {/* ---- Columna derecha: panel de repositorio ---- */}
        <Box>
          <ColumnLabel>Repositorio de Finalizados</ColumnLabel>
          <Paper sx={{ borderRadius: `${tokens.radius.lg}px`, overflow: 'hidden' }}>
          <Box sx={{ p: 2.75, pb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'grid',
                  placeItems: 'center',
                  backgroundImage: tokens.gradient.soft,
                  color: tokens.colors.indigo,
                  flexShrink: 0,
                }}
              >
                <DescriptionRoundedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={720}>
                  Archivo central
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Expedientes finalizados de tus despachos
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por caso, cliente o expediente…"
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              sx={{ my: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: tokens.colors.muted }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pb: 2 }}>
              <Chip
                label="Todos"
                size="small"
                onClick={() => setRepoOfficeFilter('')}
                sx={{
                  backgroundImage: repoOfficeFilter === '' ? tokens.gradient.soft : undefined,
                  fontWeight: 600,
                }}
              />
              {allOffices.map((o) => (
                <Chip
                  key={o.id}
                  label={o.name}
                  size="small"
                  onClick={() => setRepoOfficeFilter(o.id)}
                  sx={{
                    backgroundImage: repoOfficeFilter === o.id ? tokens.gradient.soft : undefined,
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ maxHeight: 520, overflowY: 'auto', px: 1, pb: 1 }}>
            {repoLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : filteredRepoCases.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No hay casos que coincidan.
              </Typography>
            ) : (
              filteredRepoCases.map((c) => {
                const clientName = c.client?.full_name || c.client_str || 'Sin cliente';
                return (
                  <Box
                    key={c.id}
                    onClick={() => navigate(`/office/${c.office_id}/case/${c.id}`)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: '13px',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: tokens.colors.panel2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#fff',
                        flexShrink: 0,
                        backgroundColor:
                          allOffices.find((o) => o.id === c.office_id)?.color ||
                          tokens.colors.indigo,
                      }}
                    >
                      <DescriptionRoundedIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={620} noWrap>
                        {c.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {clientName}
                        {c.office_practice_area?.name ? ` · ${c.office_practice_area.name}` : ''}
                        {` · ${c.number}`}
                      </Typography>
                    </Box>
                    {c.end_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(c.end_date).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </Typography>
                    )}
                  </Box>
                );
              })
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${tokens.colors.line}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {filteredRepoCases.length} de {repoCases.length} casos finalizados
            </Typography>
            <Box
              onClick={() => navigate(ARCHIVED_CASES)}
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 12.5,
                fontWeight: 650,
                color: tokens.colors.cyan,
                cursor: 'pointer',
              }}
            >
              Ver todo <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
            </Box>
          </Box>
        </Paper>
        </Box>
      </Box>

      <ConfirmationModal
        open={deleteModal.open}
        onClose={cancelDeleteOffice}
        onConfirm={confirmDeleteOffice}
        title="Eliminar Despacho"
        content={`¿Estás seguro de que deseas eliminar "${deleteModal.officeName}"? Esta acción no se puede deshacer y se perderán todos los datos asociados.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        severity="error"
        confirmButtonColor="error"
        loading={deleteModal.loading}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeNotification} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OfficeListView;